// @ts-nocheck
/**
 * @fileoverview Theme Main Entry Point
 *
 * This file is the main JavaScript entry point for the theme.
 * It initializes global functionality and imports necessary modules.
 */

// Import custom elements
import '@modules/Modal.module.js';
import '@modules/TopPicksCart.module.js';
import '@modules/UpsellCarousel.module.js';
import '@modules/UpsellProduct.module.js';

// Import utilities and modules
import analytics from '@modules/Analytics.module.js';
import { addToCart } from '@utils/cart.module.js';

/**
 * Handle click add to cart for non-select elements
 * @param {Event} event
 * @param {HTMLElement} element
 */
async function handleClickAddToCart(event, element) {
  const variantId = element.dataset.rtVariant;
  const quantity = parseInt(element.dataset.rtQuantity, 10) || 1;

  if (!variantId) {
    console.warn('No variant ID found on element', element);
    return;
  }

  element.disabled = true;
  element.classList.add('rt-button--loading');

  try {
    await addToCart({
      id: parseInt(variantId, 10),
      quantity,
    });

    document.dispatchEvent(
      new CustomEvent('rt:add-to-cart', {
        bubbles: true,
        detail: { variantId, quantity },
      })
    );

    setTimeout(() => {
      element.classList.remove('rt-button--loading');
      element.disabled = false;
    }, 1000);
  } catch (error) {
    console.error('Error adding to cart:', error);

    setTimeout(() => {
      element.classList.remove('rt-button--loading');
      element.disabled = false;
    }, 1000);
  }
}

/**
 * Handle change add to cart for select elements
 * @param {Event} event
 * @param {HTMLSelectElement} select
 */
async function handleSelectAddToCart(event, select) {
  const variantId = select.value || select.dataset.rtVariant;
  const quantity = parseInt(select.dataset.rtQuantity, 10) || 1;

  if (!variantId) {
    return;
  }

  select.disabled = true;

  try {
    await addToCart({
      id: parseInt(variantId, 10),
      quantity,
    });

    document.dispatchEvent(
      new CustomEvent('rt:add-to-cart', {
        bubbles: true,
        detail: { variantId, quantity },
      })
    );

    // Reset select to placeholder if exists
    if (select.options[0]?.value === '') {
      select.selectedIndex = 0;
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
  } finally {
    select.disabled = false;
  }
}

/**
 * Initialize add-to-cart elements with data-rt-add-to-cart and data-rt-variant attributes
 * Uses event delegation to support dynamically added elements
 */
function initAddToCartElements() {
  // Use event delegation on document for dynamic elements
  document.addEventListener('click', (event) => {
    const element = event.target.closest('[data-rt-add-to-cart][data-rt-variant]');
    if (element && element.tagName.toLowerCase() !== 'select') {
      handleClickAddToCart(event, element);
    }
  });

  document.addEventListener('change', (event) => {
    const element = event.target.closest('[data-rt-add-to-cart][data-rt-variant]');
    if (element && element.tagName.toLowerCase() === 'select') {
      handleSelectAddToCart(event, element);
    }
  });
}

/**
 * Initialize theme functionality
 */
async function initTheme() {
  // Initialize add-to-cart elements
  initAddToCartElements();

  // Custom Event: rt-cart:added
  document.addEventListener('rt-cart:added', () => {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    const cartBtn = document.querySelector('[aria-controls="cart-drawer"]');
    if (cartBtn && cartBtn?.ariaExpanded !== 'true') {
      cartBtn.click();
    }
  });

  // debug
  analytics.trackView('rt-product-upsell-carousel', 'rt_view_upsell_carousel');
  analytics.trackView('.rt-top-picks-cart-section', 'rt_view_top_picks_cart');
  analytics.enableDebug();

  // Emit ready event
  document.dispatchEvent(new CustomEvent('rt-theme:ready'));

  console.log('Theme initialized');
}

// Initialize on load
initTheme();
